import React, { useEffect, useState } from "react";
import { Loader2, Lock, LogOut, Mail, Phone, RefreshCw, FileText, Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from "@/components/ui/tabs";
import { adminLogin, adminMe, fetchContacts, fetchCallbacks, fetchBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from "@/lib/api";

const TOKEN_KEY = "fw_admin_token";

function LoginCard({ onLoggedIn }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            const res = await adminLogin(password);
            localStorage.setItem(TOKEN_KEY, res.token);
            toast.success("Welcome back, Gurpreet.");
            onLoggedIn();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Login failed";
            toast.error(typeof msg === "string" ? msg : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center bg-[#F3F1EA]">
            <form onSubmit={submit} data-testid="admin-login-form" className="bg-white border border-border rounded-sm p-10 w-full max-w-md">
                <div className="w-12 h-12 rounded-sm bg-[#1A362B] text-white grid place-items-center mx-auto">
                    <Lock className="w-5 h-5" />
                </div>
                <h1 className="font-serif text-3xl text-[#1A362B] text-center mt-6">Admin · Finance Wealth</h1>
                <div className="divider-thin mx-auto my-5" />
                <p className="text-center text-sm text-[#1C1C1C]/70">
                    Enter your password to view enquiries.
                </p>

                <div className="mt-8">
                    <Label htmlFor="admin-password" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Password</Label>
                    <Input
                        id="admin-password"
                        data-testid="admin-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="mt-2 h-12 border-border"
                        autoFocus
                    />
                </div>

                <button
                    type="submit"
                    data-testid="admin-login-submit"
                    disabled={loading || !password}
                    className="btn-primary w-full mt-6 disabled:opacity-60 disabled:pointer-events-none"
                >
                    {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : "Sign in"}
                </button>
            </form>
        </div>
    );
}

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleString("en-AU", {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        });
    } catch { return iso; }
}

function Dashboard({ onLogout }) {
    const [contacts, setContacts] = useState([]);
    const [callbacks, setCallbacks] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [c, cb] = await Promise.all([fetchContacts(), fetchCallbacks()]);
            setContacts(c || []);
            setCallbacks(cb || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                onLogout();
            } else {
                toast.error("Failed to load enquiries");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <div className="bg-background min-h-screen pt-24">
            <div className="container-prose pb-24">
                <div className="flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
                    <div>
                        <div className="overline">Admin</div>
                        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-[#1A362B] tracking-tight font-medium">Enquiries dashboard</h1>
                        <p className="mt-3 text-[#1C1C1C]/70">All website enquiries and call-back requests.</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={load} data-testid="admin-refresh" className="btn-outline">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                        <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center justify-center gap-2 border border-border text-[#1C1C1C]/70 px-7 py-3.5 rounded-sm font-medium text-sm tracking-wide hover:bg-[#F3F1EA] transition">
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white border border-border p-6 rounded-sm">
                        <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Contact enquiries</div>
                        <div className="mt-2 font-serif text-4xl text-[#1A362B]" data-testid="stat-contacts">{contacts.length}</div>
                    </div>
                    <div className="bg-white border border-border p-6 rounded-sm">
                        <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Call-back requests</div>
                        <div className="mt-2 font-serif text-4xl text-[#1A362B]" data-testid="stat-callbacks">{callbacks.length}</div>
                    </div>
                    <div className="bg-[#1A362B] text-white p-6 rounded-sm">
                        <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Total</div>
                        <div className="mt-2 font-serif text-4xl">{contacts.length + callbacks.length}</div>
                    </div>
                </div>

                <Tabs defaultValue="contacts" className="mt-10">
                    <TabsList className="bg-[#F3F1EA] p-1 rounded-sm">
                        <TabsTrigger value="contacts" data-testid="tab-contacts" className="data-[state=active]:bg-white data-[state=active]:text-[#1A362B]">Contact enquiries</TabsTrigger>
                        <TabsTrigger value="callbacks" data-testid="tab-callbacks" className="data-[state=active]:bg-white data-[state=active]:text-[#1A362B]">Call-back requests</TabsTrigger>
                        <TabsTrigger value="blog" data-testid="tab-blog" className="data-[state=active]:bg-white data-[state=active]:text-[#1A362B]">Blog</TabsTrigger>
                    </TabsList>

                    <TabsContent value="contacts" className="mt-6">
                        {loading ? (
                            <div className="py-20 text-center text-[#1C1C1C]/50"><Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…</div>
                        ) : contacts.length === 0 ? (
                            <div className="bg-white border border-border rounded-sm p-16 text-center text-[#1C1C1C]/60">No contact enquiries yet.</div>
                        ) : (
                            <div className="bg-white border border-border rounded-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-[#F3F1EA] text-left text-[10px] tracking-[0.2em] uppercase text-[#1A362B]">
                                                <th className="px-5 py-4 font-semibold">Received</th>
                                                <th className="px-5 py-4 font-semibold">Name</th>
                                                <th className="px-5 py-4 font-semibold">Contact</th>
                                                <th className="px-5 py-4 font-semibold">Loan type</th>
                                                <th className="px-5 py-4 font-semibold">Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contacts.map((c) => (
                                                <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-t border-border hover:bg-[#FAF9F6] align-top">
                                                    <td className="px-5 py-4 text-[#1C1C1C]/60 whitespace-nowrap text-xs">{fmtDate(c.created_at)}</td>
                                                    <td className="px-5 py-4 text-[#1A362B] font-medium">{c.full_name}</td>
                                                    <td className="px-5 py-4">
                                                        <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[#1A362B] hover:text-[#C69C6D] transition"><Mail className="w-3.5 h-3.5" /> {c.email}</a>
                                                        <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-[#1A362B] hover:text-[#C69C6D] transition mt-1"><Phone className="w-3.5 h-3.5" /> {c.phone}</a>
                                                    </td>
                                                    <td className="px-5 py-4 text-[#1C1C1C]/80">{c.loan_type || "—"}</td>
                                                    <td className="px-5 py-4 text-[#1C1C1C]/80 max-w-xs whitespace-pre-wrap">{c.message || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="callbacks" className="mt-6">
                        {loading ? (
                            <div className="py-20 text-center text-[#1C1C1C]/50"><Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…</div>
                        ) : callbacks.length === 0 ? (
                            <div className="bg-white border border-border rounded-sm p-16 text-center text-[#1C1C1C]/60">No call-back requests yet.</div>
                        ) : (
                            <div className="bg-white border border-border rounded-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-[#F3F1EA] text-left text-[10px] tracking-[0.2em] uppercase text-[#1A362B]">
                                                <th className="px-5 py-4 font-semibold">Received</th>
                                                <th className="px-5 py-4 font-semibold">Name</th>
                                                <th className="px-5 py-4 font-semibold">Phone</th>
                                                <th className="px-5 py-4 font-semibold">Post code</th>
                                                <th className="px-5 py-4 font-semibold">Preferred time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {callbacks.map((c) => (
                                                <tr key={c.id} data-testid={`callback-row-${c.id}`} className="border-t border-border hover:bg-[#FAF9F6]">
                                                    <td className="px-5 py-4 text-[#1C1C1C]/60 whitespace-nowrap text-xs">{fmtDate(c.created_at)}</td>
                                                    <td className="px-5 py-4 text-[#1A362B] font-medium">{c.first_name} {c.last_name}</td>
                                                    <td className="px-5 py-4"><a href={`tel:${c.phone}`} className="text-[#1A362B] hover:text-[#C69C6D]">{c.phone}</a></td>
                                                    <td className="px-5 py-4 text-[#1C1C1C]/80">{c.post_code || "—"}</td>
                                                    <td className="px-5 py-4 text-[#1C1C1C]/80">{c.preferred_time || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="blog" className="mt-6">
                        <BlogManager onAuthError={onLogout} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

const EMPTY_POST = {
    title: "", excerpt: "", content: "", cover_image: "", category: "Guides",
    meta_description: "", keywords: "", published: true
};

function BlogManager({ onAuthError }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | slug
    const [form, setForm] = useState(EMPTY_POST);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        fetchBlogPosts(true)
            .then(setPosts)
            .catch((err) => { if (err?.response?.status === 401) onAuthError(); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const startNew = () => { setForm(EMPTY_POST); setEditing("new"); };
    const startEdit = (p) => {
        setForm({
            title: p.title || "", excerpt: p.excerpt || "", content: p.content || "",
            cover_image: p.cover_image || "", category: p.category || "Guides",
            meta_description: p.meta_description || "", keywords: p.keywords || "",
            published: p.published !== false
        });
        setEditing(p.slug);
    };
    const cancel = () => { setEditing(null); setForm(EMPTY_POST); };
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title is required."); return; }
        setSaving(true);
        try {
            if (editing === "new") {
                await createBlogPost(form);
                toast.success("Article published.");
            } else {
                await updateBlogPost(editing, form);
                toast.success("Article updated.");
            }
            cancel();
            load();
        } catch (err) {
            if (err?.response?.status === 401) onAuthError();
            else toast.error(err?.response?.data?.detail || "Could not save article.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (slug) => {
        if (!window.confirm("Delete this article permanently?")) return;
        try {
            await deleteBlogPost(slug);
            toast.success("Article deleted.");
            load();
        } catch (err) {
            if (err?.response?.status === 401) onAuthError();
            else toast.error("Could not delete article.");
        }
    };

    if (editing) {
        return (
            <div data-testid="blog-editor" className="bg-white border border-border rounded-sm p-8 space-y-5">
                <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl text-[#1A362B]">{editing === "new" ? "New article" : "Edit article"}</h3>
                    <button onClick={cancel} data-testid="blog-editor-close" className="text-[#1C1C1C]/50 hover:text-[#1A362B]"><X className="w-5 h-5" /></button>
                </div>
                <div>
                    <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Title</Label>
                    <Input data-testid="blog-input-title" value={form.title} onChange={upd("title")} className="mt-2 h-11 border-border" placeholder="e.g. First Home Buyer Guide 2026" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Category</Label>
                        <Input data-testid="blog-input-category" value={form.category} onChange={upd("category")} className="mt-2 h-11 border-border" placeholder="Home Loans" />
                    </div>
                    <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Cover image URL</Label>
                        <Input data-testid="blog-input-cover" value={form.cover_image} onChange={upd("cover_image")} className="mt-2 h-11 border-border" placeholder="https://..." />
                    </div>
                </div>
                <div>
                    <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Excerpt (shown on blog cards)</Label>
                    <Textarea data-testid="blog-input-excerpt" value={form.excerpt} onChange={upd("excerpt")} rows={2} className="mt-2 border-border resize-none" />
                </div>
                <div>
                    <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Content (Markdown supported)</Label>
                    <Textarea data-testid="blog-input-content" value={form.content} onChange={upd("content")} rows={14} className="mt-2 border-border font-mono text-sm" placeholder="## Heading&#10;&#10;Write your article using Markdown..." />
                    <p className="text-xs text-[#1C1C1C]/50 mt-1">Use ## for headings, - for bullet lists, **bold**, [link](/contact), &gt; for quotes.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Meta description (SEO)</Label>
                        <Textarea data-testid="blog-input-meta" value={form.meta_description} onChange={upd("meta_description")} rows={2} className="mt-2 border-border resize-none" />
                    </div>
                    <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Keywords (SEO, comma-separated)</Label>
                        <Textarea data-testid="blog-input-keywords" value={form.keywords} onChange={upd("keywords")} rows={2} className="mt-2 border-border resize-none" />
                    </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#1A362B]">
                    <input type="checkbox" data-testid="blog-input-published" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
                    Published (visible on the public blog)
                </label>
                <div className="flex gap-3 pt-2">
                    <button onClick={save} disabled={saving} data-testid="blog-save" className="btn-primary disabled:opacity-60 disabled:pointer-events-none">
                        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> {editing === "new" ? "Publish article" : "Save changes"}</>}
                    </button>
                    <button onClick={cancel} data-testid="blog-cancel" className="btn-outline">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-[#1C1C1C]/60">{posts.length} article{posts.length === 1 ? "" : "s"}</p>
                <button onClick={startNew} data-testid="blog-new" className="btn-primary"><Plus className="w-4 h-4" /> New article</button>
            </div>
            {loading ? (
                <div className="py-20 text-center text-[#1C1C1C]/50"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
            ) : posts.length === 0 ? (
                <div className="bg-white border border-border rounded-sm p-16 text-center text-[#1C1C1C]/60">No articles yet. Click &ldquo;New article&rdquo; to write your first.</div>
            ) : (
                <div className="bg-white border border-border rounded-sm divide-y divide-border">
                    {posts.map((p) => (
                        <div key={p.slug} data-testid={`blog-row-${p.slug}`} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#FAF9F6]">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-[#C69C6D] shrink-0" />
                                    <span className="font-medium text-[#1A362B] truncate">{p.title}</span>
                                    {p.published === false && <span className="text-[10px] uppercase tracking-wide bg-[#EBE8DF] text-[#1C1C1C]/60 px-2 py-0.5 rounded">Draft</span>}
                                </div>
                                <div className="text-xs text-[#1C1C1C]/50 mt-1 ml-6">{p.category} · /blog/{p.slug}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button onClick={() => startEdit(p)} data-testid={`blog-edit-${p.slug}`} className="text-sm px-4 py-2 border border-border rounded-sm hover:bg-[#F3F1EA] text-[#1A362B]">Edit</button>
                                <button onClick={() => remove(p.slug)} data-testid={`blog-delete-${p.slug}`} className="w-9 h-9 grid place-items-center border border-border rounded-sm hover:bg-red-50 hover:border-red-200 text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminPage() {
    const [authed, setAuthed] = useState(null); // null = checking, false = no, true = yes

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) { setAuthed(false); return; }
        adminMe()
            .then(() => setAuthed(true))
            .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    }, []);

    const onLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
    };

    if (authed === null) {
        return (
            <div className="pt-24 bg-background min-h-screen">
                <div className="container-prose py-24 text-center text-[#1C1C1C]/50">
                    <Loader2 className="w-6 h-6 animate-spin inline-block" />
                </div>
            </div>
        );
    }
    if (!authed) return <div className="pt-20" data-testid="page-admin"><LoginCard onLoggedIn={() => setAuthed(true)} /></div>;
    return <div data-testid="page-admin-dashboard"><Dashboard onLogout={onLogout} /></div>;
}
